import { format } from 'date-fns';
import { ChevronRight, FileText, FlaskConical, Paperclip, Pill, Stethoscope } from 'lucide-react';

import { TagChip } from './TagChip';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { hasLabTag, hasRxTag } from '../utils/tagMatchers';
import type { TimelineItem as TimelineItemType } from '../types';
import {
  documentStatusColors,
  encounterTypeColors,
  extractionStatusColors,
  timelineDotColors,
  uploadedDocumentTypeLabels,
} from '../constants';

interface TimelineItemProps {
  isLast: boolean;
  item: TimelineItemType;
  onClick: (item: TimelineItemType) => void;
}

export function TimelineItem({ item, isLast, onClick }: TimelineItemProps) {
  const visualKind = visualKindFor(item);
  const dotColor = timelineDotColors[visualKind] ?? 'bg-gray-400';
  const badge = getBadge(item);

  return (
    <div className="relative flex gap-4">
      <div className="flex flex-col items-center">
        <div className={`h-3 w-3 rounded-full mt-1.5 shrink-0 ${dotColor}`} />
        {!isLast && <div className="w-px flex-1 bg-border mt-1" />}
      </div>

      <Card className="flex-1 mb-2 cursor-pointer hover:shadow-md transition-shadow" onClick={() => onClick(item)}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                {getIcon(item)}
                <span className="font-medium">{getTitle(item)}</span>
                {badge && (
                  <Badge variant="secondary" className={`text-xs ${badge.className}`}>
                    {badge.label}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-0.5 ml-6">{getSubtitle(item)}</p>
              {(() => {
                const tags = tagsForItem(item);
                if (tags.length === 0) return null;
                return (
                  <div className="flex flex-wrap gap-1 mt-1.5 ml-6">
                    {tags.map((tag) => (
                      <TagChip key={tag.id} tag={tag} />
                    ))}
                  </div>
                );
              })()}
              {item.kind === 'encounter' &&
                item.data.clinical_context &&
                (item.data.clinical_context as { chief_complaint?: string }).chief_complaint && (
                  <p className="text-sm mt-2 ml-6 line-clamp-2">
                    {(item.data.clinical_context as { chief_complaint?: string }).chief_complaint}
                  </p>
                )}
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function EncounterIcon() {
  return <Stethoscope className="h-4 w-4 text-green-600" />;
}

function GeneratedIcon() {
  return <FileText className="h-4 w-4 text-blue-600" />;
}

function getBadge(item: TimelineItemType): { label: string; className: string } | null {
  switch (item.kind) {
    case 'encounter':
      if (item.data.encounter_type) {
        return {
          label: item.data.encounter_type,
          className: encounterTypeColors[item.data.encounter_type] ?? '',
        };
      }
      return null;
    case 'generated':
      if (item.data.status === 'draft' || item.data.status === 'generating') {
        return {
          label: item.data.status,
          className: documentStatusColors[item.data.status] ?? '',
        };
      }
      return null;
    case 'lab':
    case 'prescription':
    case 'uploaded': {
      const extractionStatus = item.data.current_revision?.extraction_status;
      if (extractionStatus === 'extracting') {
        return {
          label: 'Extracting',
          className: extractionStatusColors.extracting,
        };
      }
      if (extractionStatus === 'failed') {
        return {
          label: 'Extraction failed',
          className: extractionStatusColors.failed,
        };
      }
      return null;
    }
  }
}

function getIcon(item: TimelineItemType) {
  const kind = visualKindFor(item);
  switch (kind) {
    case 'encounter':
      return <EncounterIcon />;
    case 'generated':
      return <GeneratedIcon />;
    case 'lab':
      return <LabIcon />;
    case 'prescription':
      return <PrescriptionIcon />;
    case 'uploaded':
      return <UploadedIcon />;
  }
}

function getSubtitle(item: TimelineItemType): string {
  switch (item.kind) {
    case 'encounter': {
      return format(new Date(item.data.encounter_date), 'MMMM d, yyyy');
    }
    case 'generated': {
      return `Generated ${format(new Date(item.data.generated_at), "MMM d, yyyy 'at' h:mm a")}`;
    }
    case 'lab':
    case 'prescription':
    case 'uploaded': {
      const typeLabel = uploadedDocumentTypeLabels[item.data.document_type] ?? item.data.document_type;
      return `${typeLabel} \u00B7 ${format(new Date(item.data.document_date), 'MMM d, yyyy')}`;
    }
  }
}

function getTitle(item: TimelineItemType): string {
  switch (item.kind) {
    case 'encounter': {
      const type = item.data.encounter_type ?? 'Visit';
      const clinician = item.data.clinician?.name;
      return clinician ? `${type} \u00B7 ${clinician}` : type;
    }
    case 'generated': {
      return item.data.template?.name || 'Generated Document';
    }
    case 'lab':
    case 'prescription':
    case 'uploaded': {
      return item.data.name;
    }
  }
}

function LabIcon() {
  return <FlaskConical className="h-4 w-4 text-purple-600" />;
}

function PrescriptionIcon() {
  return <Pill className="h-4 w-4 text-rose-600" />;
}

/** Tags only render on uploaded + generated rows (encounters not taggable per Q3). */
function tagsForItem(item: TimelineItemType) {
  if (item.kind === 'encounter') return [];
  return item.data.tags ?? [];
}

function UploadedIcon() {
  return <Paperclip className="h-4 w-4 text-amber-600" />;
}

/**
 * Resolve the visual variant a timeline item should render as. Lab and
 * prescription uploads keep their distinct icon/label even when they appear
 * under the generic "uploaded" kind (for the "All" filter view).
 *
 * Plan 08: an uploaded doc reads as lab/prescription if its
 * `document_type` matches OR any of its tags is a canonical match.
 * This stays in sync with the filter predicates in `UnifiedTimeline`.
 */
function visualKindFor(item: TimelineItemType): TimelineItemType['kind'] {
  if (item.kind === 'uploaded') {
    if (item.data.document_type === 'lab_result' || hasLabTag(item.data.tags)) return 'lab';
    if (item.data.document_type === 'prescription' || hasRxTag(item.data.tags)) return 'prescription';
  }
  return item.kind;
}
